/*
 **	OMNI_IQ (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **/

import { useState, useEffect } from "react"
import { Outlet, useOutletContext, Navigate } from "react-router-dom"

import { useAuth0 } from "@auth0/auth0-react"

import type { ApplicationContextType } from "../components/common/types"
import EmailVerificationRequired from "../components/common/EmailVerificationRequired"
import LoadingPage from "./LoadingPage"
import CalibrationLayout from "./CalibrationLayout"

import Config from "../components/common/Config"
import { featureFlags } from "../components/common/Features"
import { logger } from "../utils/logger"

type applicationType = {
  context: {
    application: ApplicationContextType
    logout: Function
  }
}

export function useContext() {
  return useOutletContext<applicationType>()
}

export default function AuthenticationState(): JSX.Element {
  const [context, setContext] = useState<ApplicationContextType | null>(null)

  // utilize Auth0 for Login
  const {
    loginWithRedirect,
    user,
    isLoading,
    isAuthenticated,
    logout,
    getAccessTokenSilently,
  } = useAuth0()

  function onLogout(): void {
    setContext(null)
    // logout from Auth0
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  // Periodic token validation check
  useEffect(() => {
    if (!isAuthenticated || !context) return

    // Check token validity every 5 minutes
    const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes

    const checkTokenValidity = async () => {
      try {
        // Auth0 SDK will use cached token if valid, or refresh automatically if needed
        await getAccessTokenSilently({
          authorizationParams: {
            audience: Config.AUTH0_AUDIENCE,
            scope: "openid profile email read:devices read:device_data read:locations read:events read:alarms"
          }
        })
      } catch (error: any) {
        logger.warn('Token validation failed - redirecting to login')

        // Token is invalid or expired, redirect to login
        loginWithRedirect({
          appState: {
            returnTo: window.location.pathname,
          },
        })
      }
    }

    // Initial check after 1 minute
    const initialTimer = setTimeout(checkTokenValidity, 60000)

    // Then check periodically
    const intervalId = setInterval(checkTokenValidity, TOKEN_CHECK_INTERVAL)

    return () => {
      clearTimeout(initialTimer)
      clearInterval(intervalId)
    }
  }, [isAuthenticated, context, getAccessTokenSilently, loginWithRedirect])

  // async load login information
  useEffect(() => {
    // Only proceed if authenticated
    if (!isAuthenticated) {
      return
    }

    if (isAuthenticated && user && !context) {
      getAccessTokenSilently({
        authorizationParams: {
          audience: Config.AUTH0_AUDIENCE,
          scope: "openid profile email read:devices read:device_data read:locations read:events read:alarms"
        }
      }).then(creds => {
        const userToken = `auth0|${creds}`;

        // Call 'me' query to ensure user is provisioned in database
        // This enables auto-provisioning for users who exist in Auth0 but not in OmniIQ DB
        const namespaceId = user.user_metadata?.ns_id;
        if (namespaceId) {
          fetch(Config.STRATA47_OMNIIQ_API, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${creds}`,
              'x-bp-token': Config.STRATA47_BP_TOKEN,
              'X-Client-Type': 'omniiq-web-client',
            },
            body: JSON.stringify({
              query: `query { me { id email fullName } }`
            })
          }).catch(err => {
            logger.error('Failed to call me query for user provisioning:', err);
          });
        }

        setContext({
          environment: Config.STRATA47_MB_ENV,
          userToken,
          userProfile: user,
          namespace: {
            namespaceName: user.user_metadata.ns_name,
            namespaceId: user.user_metadata.ns_id,
            namespaceTitle: user.user_metadata.ns_title,
            namespaceDescription: user.user_metadata.ns_description,
          },
          features: featureFlags,
        })
      }).catch(error => {
        logger.error('Main: Failed to acquire access token:', error);
      })
    }
  }, [isAuthenticated])

  // Redirect to landing page if not authenticated
  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      {isAuthenticated &&
      !isLoading &&
      context &&
      user &&
      user.email_verified === true ? (
        <CalibrationLayout context={context} onLogout={onLogout}>
          <Outlet
            context={
              { context: { application: context, logout: onLogout } } satisfies applicationType
            }
          />
        </CalibrationLayout>
      ) : null}

      {isAuthenticated &&
      !isLoading &&
      context &&
      user &&
      user.email_verified === false ? (
        <EmailVerificationRequired />
      ) : null}

      {!isAuthenticated && isLoading ? <LoadingPage /> : null}
    </>
  )
}
